import React from 'react';
import { getColorIndex, isEmpty } from '../../helpers/common';
import SVGIcon from '../../assets/images/svg/SVGIcon';
import Avatar from '../avatar';
import {
  AVATAR_BORDER,
  CHIP_SIZE,
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_COLORS,
  ICON_SIZE
} from '../../global/constants';
import IconButton from '../button/iconButton';
import { ChipIcon, ChipLeftSection, ChipText, ChipWrapper } from './styles';
import { ProjectModel } from '../../mobx/models/project';
import ProjectIconAvatar from '../projectIconAvatar';

/**
 * Props interface for the Chip component
 * @param {string} title - The main text content displayed in the chip
 * @param {number} variant - The visual variant/style type of the chip
 * @param {number} size - The size of the chip (small/medium)
 * @param {string} [leadingIconName] - Optional icon name to display on the left side of the chip
 * @param {string} [leadingIconViewBox] - Optional icon viewBox to display on the left side of the chip
 * @param {string} [trailingIconName] - Optional icon name to display on the right side of the chip
 * @param {string} [trailingIconViewBox] - Optional icon viewBox to display on the right side of the chip
 * @param {string} [className] - Optional CSS class name for custom styling
 * @param {() => void} [onClick] - Optional click handler function for the chip
 * @param {boolean} [isDisabled] - Optional boolean to determine if the chip is disabled
 * @param {boolean} [isActive] - Optional boolean to determine if the chip is active
 * @param {string} [leadingIconButtonTitle] - Optional title for the leading icon button
 * @param {string} [trailingIconButtonTitle] - Optional title for the trailing icon button
 * @param {string} [trailingIconClassName] - Optional CSS class name for styling the trailing icon
 * @param {string} [avatarImgSrc] - Optional image source URL for avatar display
 * @param {string} [avatarName] - Optional name for avatar fallback display
 * @param {'primary' | 'secondary'} [iconColorType] - Optional color type for icons
 * @param {'primary' | 'secondary'} [textColorType] - Optional color type for the chip text
 * @param {number} [iconPosition] - Optional position configuration for icons
 * @param {boolean} [disableHoverEffect] - Optional boolean to determine if the hover effect is disabled
 * @param {boolean} [isInputField] - Optional boolean to determine if the chip is used as an input field
 * @param {() => void} [onLeadingIconButtonClick] - Optional click handler for the leading icon button
 * @param {(e: React.MouseEvent<HTMLButtonElement>) => void}
 * [onTrailingIconButtonClick] - Optional click handler for the trailing icon button
 * @param {string} [iconButtonClassName] - Optional CSS class name for the icon button
 * @param {boolean} [isMulticolorIcon] - Optional flag indicating if the icon uses multiple colors
 * @param {ProjectModel['icon']} [projectIcon] - Optional project icon configuration for project chips
 * @param {string} [projectName] - Optional project name to display with project icon
 *
 * */
interface Props {
  title?: string;
  variant: number;
  size: number;
  leadingIconName?: string;
  leadingIconViewBox?: string;
  trailingIconName?: string;
  trailingIconViewBox?: string;
  className?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  isActive?: boolean;
  leadingIconButtonTitle?: string;
  trailingIconButtonTitle?: string;
  trailingIconClassName?: string;
  avatarImgSrc?: string;
  avatarName?: string;
  iconColorType?: 'primary' | 'secondary';
  textColorType?: 'primary' | 'secondary';
  iconPosition?: number;
  onLeadingIconButtonClick?: () => void;
  onTrailingIconButtonClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disableHoverEffect?: boolean;
  iconButtonClassName?: string;
  isInputField?: boolean;
  isMulticolorIcon?: boolean;
  projectIcon?: ProjectModel['icon'];
  projectName?: string;
}

const ChipComponent: React.FC<Props> = ({
  title,
  leadingIconName,
  leadingIconViewBox,
  trailingIconName,
  trailingIconViewBox,
  className,
  onClick,
  isActive,
  isDisabled,
  avatarImgSrc,
  avatarName,
  trailingIconClassName,
  leadingIconButtonTitle,
  trailingIconButtonTitle,
  variant,
  size = CHIP_SIZE.SMALL,
  iconColorType = 'secondary',
  textColorType = 'primary',
  iconPosition,
  onLeadingIconButtonClick,
  onTrailingIconButtonClick,
  disableHoverEffect,
  iconButtonClassName,
  isInputField,
  isMulticolorIcon,
  projectIcon,
  projectName
}) => {
  const showAvatar = !isEmpty(avatarImgSrc) || !isEmpty(avatarName);
  const showProjectAvatar = !isEmpty(projectIcon) || !isEmpty(projectName);

  return (
    <>
      <ChipWrapper
        role='button'
        disableHoverEffect={disableHoverEffect}
        className={isInputField ? 'profile-tag' : className}
        onClick={onClick}
        isActive={isActive}
        isDisabled={isDisabled}
        variant={variant}
        size={size}
        iconPosition={iconPosition}
        isAvatar={showAvatar}
        isInputField={isInputField}
        isMulticolorIcon={isMulticolorIcon}>
        {!isEmpty(leadingIconButtonTitle) && (
          <IconButton
            iconName={leadingIconButtonTitle || ''}
            size={ICON_BUTTON_SIZE.EXTRA_X_SMALL}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            iconColorType={iconColorType}
            onClick={onLeadingIconButtonClick}
            className={iconButtonClassName}
          />
        )}
        <ChipLeftSection size={size}>
          {!isEmpty(leadingIconName) && (
            <ChipIcon isLeadingIcon={true}>
              <SVGIcon
                name={leadingIconName}
                width={ICON_SIZE.MD}
                height={ICON_SIZE.MD}
                viewBox={leadingIconViewBox || `0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill={
                  isMulticolorIcon
                    ? undefined
                    : iconColorType === 'primary'
                    ? 'var(--text-primary)'
                    : 'var(--text-secondary)'
                }
              />
            </ChipIcon>
          )}
          {/* shows project avatar */}
          {showProjectAvatar && (
            <ProjectIconAvatar
              type={projectIcon?.type}
              reference={projectIcon?.reference || projectName || ''}
              backgroundColor={
                projectIcon?.color ? ICON_COLORS[projectIcon?.color] : ICON_COLORS[getColorIndex(projectName || '')]
              }
              projectName={projectName || ''}
              size={18}
              iconSize={13}
              fontSize='13px'
              lineHeight='18px'
              borderRadius='4px'
            />
          )}
          {/* shows regular avatar */}
          {showAvatar && (
            <Avatar
              imgSrc={avatarImgSrc || ''}
              name={avatarName || 'U N'}
              size={18}
              borderVariant={AVATAR_BORDER.NONE}
            />
          )}
          {title && <ChipText textColorType={textColorType}>{title}</ChipText>}
        </ChipLeftSection>
        {!isEmpty(trailingIconName) && (
          <ChipIcon className={trailingIconClassName}>
            <SVGIcon
              name={trailingIconName}
              width={ICON_SIZE.LG}
              height={ICON_SIZE.LG}
              viewBox={trailingIconViewBox || `0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
              fill={iconColorType === 'primary' ? 'var(--text-primary)' : 'var(--text-secondary)'}
            />
          </ChipIcon>
        )}
        {!isEmpty(trailingIconButtonTitle) && (
          <IconButton
            iconName={trailingIconButtonTitle || ''}
            size={ICON_BUTTON_SIZE.EXTRA_X_SMALL}
            variant={ICON_BUTTON_TYPE.TERTIARY}
            iconColorType={iconColorType}
            onClick={onTrailingIconButtonClick}
            className={isInputField ? 'profile-icon-button' : iconButtonClassName}
          />
        )}
      </ChipWrapper>
    </>
  );
};

export default ChipComponent;
